<script setup>
  import { computed, onMounted, onUnmounted, watch, ref } from "vue";
  import { useRouter, useRoute } from 'vue-router';
  import { useLayoutComponents } from '@/composables/layout.js';
  import { useStorage } from '@/composables/use-storage';
  import { useIcons } from '@/composables/icons.js';
  import { useEvals } from '@/composables/use-evals';
  import { fetchSellers, fetchSellerById, updateDatasetOwner } from '@/services/api';
  import { useToast } from '@/composables/use-toast';
  
  // Imports
  import { useEvalForm } from '@/composables/useEvalForm';
  import { getFileMetadata } from '@/pages/eval/eval-builder/utils/fileUtils';
  import { exampleTask, agenticExampleTask } from '@/pages/eval/eval-builder/data/exampleTask.js';
  import Sidebar from "@/pages/eval/eval-builder/components/Sidebar.vue";
  import TaskEditor from "@/pages/eval/eval-builder/components/TaskEditor.vue";
  import PreviewResults from "@/pages/eval/eval-builder/components/PreviewResults.vue";
  import { EVAL_TYPES, isValidEvalType } from '@/pages/eval/eval-builder/constants/evalTypes';
  import "./styles.css";

  const props = defineProps({
    evalType: {
      type: String,
      default: EVAL_TYPES.QA
    }
  });
  
  const { TextInput, SelectInput, Alert, AutoSaveModal } = useLayoutComponents();
  const router = useRouter();
  const route = useRoute();
  const { userInfo } = useStorage();
  const { User } = useIcons();
  const { addToast } = useToast();
  
  
  const {
    // State
    evalName, selectedOwner, originalOwner, originalOwnerName, tasks, activeTaskId, coreDatasetFile, existingCoreDatasetInfo, versionsOptions, selectedVersionId,
    isSaving, isLoadingDataset, editingDatasetId, savedSuccessfully, createdDatasetId, 
    // hasUploadError, 
    lastVersion,
    // Actions
    initializeForm, loadDataset, createEval, updateEval, 
    addTask, deleteTask, updateTask, clearVersions, revertVersion
  } = useEvalForm();
  

  const {
    evalStatus,
    evalError,
    initializeEvalBuilder,
    createEvalWithWebSocket,
    loadRecoverySessionDataFromCache,
    saveEvalPreviewDataToStorage,
    clearPreviewEvalPayload,
    flushEvalBuilderRecoverySessionToCache
  } = useEvals();
  
  const isPreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";
  const isAgentic = computed(() => props.evalType === EVAL_TYPES.AGENTIC);
  const ownersList = ref([]);
  const isAdmin = computed(() => userInfo.value?.is_admin === true);
  const showAlert = ref(false);
  const alertTitle = ref('');
  const alertMessage = ref('');
  const alertType = ref('info');
  const showUpdateOwnerAlert = ref(false);
  const showUpdateListingAlert = ref(false);
  
  const showAutosaveAlert = ref(false);
  const autosaveAlertTitle = ref('Saved draft found');
  const autosaveAlertMessage = ref('');
  const autosaveAlertConfirmText = ref('');
  const autosaveAlertCancelText = ref('');
  const lastUpdated = ref(null);
  const isAutosaveChoicePending = ref(false);
  const pendingPreviewData = ref(null);
  const pendingPreviewEvalType = ref(null);
  const showChangeEvalTypeAlert = ref(false);
  // Computed Properties
  const username = computed(() => {
    if (isAdmin.value && selectedOwner.value) {
      const owner = ownersList.value.find(owner => owner.value === selectedOwner.value);
      return owner ? owner.label : '';
    }
    return userInfo.value?.username || 'User';
  });
  
  const profilePic = computed(() => localStorage.getItem('profilePicture'));
  const selectedExampleTask = computed(() => (isAgentic.value ? agenticExampleTask : exampleTask));
  const activeTask = computed(() => {
    if (showExampleMode.value && activeTaskId.value === selectedExampleTask.value.id) {
      return selectedExampleTask.value;
    }
    return tasks.value.find(t => t.id === activeTaskId.value) ?? null;
  });
  const answeredCount = computed(() => tasks.value.filter(t => (t.answer || '').trim().length > 0).length);
  const isLoading = computed(() => isSaving.value || isLoadingDataset.value);
  const isEditing = computed(() => !!editingDatasetId.value);
  const isRevertVersionMode = computed(() => isEditing.value && versionsOptions.value.length >= 1 && selectedVersionId.value < lastVersion.value);
  const canChangeEvalType = computed(() => !route.query.datasetId);
  const saveButtonText = computed(() => {
    if (isRevertVersionMode.value) return 'Revert Version';
    return isEditing.value ? 'Save Changes' : 'Create Eval Dataset';
  });
  const changeEvalTypeAlertMessage = computed(() => {
    const currentLabel = isAgentic.value ? 'Agentic' : 'Q&A';
    return `You are currently in <strong>${currentLabel}</strong> mode. We'll keep your draft, but mode-specific settings may be hidden until you switch back. Continue?`;
  });
  
  const showExampleMode = ref(false);
  const blockedEditEval = ref(false);
  const evalTypeLabelMap = {
    [EVAL_TYPES.QA]: 'Q&A',
    [EVAL_TYPES.AGENTIC]: 'Agentic',
  };

  const handleToggleAnsweredFilter = (value) => {
    showExampleMode.value = value;
    if (value) {
      activeTaskId.value = selectedExampleTask.value.id;
    } else {
      activeTaskId.value = tasks.value[0]?.id || null;
    }
  };
  
  const hasValidPreviewData = (previewData) => {
    const hasEvalName = !!(previewData?.evalName && previewData.evalName.trim().length > 0);
    const hasTaskPromptOrAnswer = !!previewData?.tasks?.some((t) => (
      (t?.prompt && t.prompt.trim().length > 0) ||
      (t?.answer && t.answer.trim().length > 0)
    ));

    // Only treat autosave as valid if the user actually typed something
    // (non-empty eval name OR at least one task with prompt/answer content).
    return hasEvalName || hasTaskPromptOrAnswer;
  };

  const applyPreviewData = (previewData) => {
    lastUpdated.value = previewData.meta?.lastUpdated;
    if (previewData.evalName) evalName.value = previewData.evalName;
    if (previewData.tasks?.length) {
      tasks.value = previewData.tasks;
      activeTaskId.value = previewData.tasks[0]?.id || null;
    } else {
      // If no tasks in storage but evalName exists, ensure we have at least one empty task
      const emptyTask = {
        id: crypto.randomUUID(),
        prompt: "",
        answer: "",
        references: [],
        tools: [],
        criteria: [],
        environment: {
          allowedTools: '',
          maxTurns: 6,
          notes: ''
        },
        passThreshold: 70
      };
      tasks.value = [emptyTask];
      activeTaskId.value = emptyTask.id;
    }
    if (previewData.ownerId) selectedOwner.value = previewData.ownerId;
  };

  const parsePreviewData = (storedPreview) => {
    if (!storedPreview) return null;
    const previewData = JSON.parse(storedPreview);
    return hasValidPreviewData(previewData) ? previewData : null;
  };

  const formatAutosaveTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const parsedDate = new Date(timestamp);
    if (Number.isNaN(parsedDate.getTime())) return '';

    const datePart = parsedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit'
    });

    const timePart = parsedDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });

    return `${datePart} <div class="inline-block text-blue-500/60">•</div> ${timePart}`;
  };

  const getEvalTypeLabel = (type) => evalTypeLabelMap[type] || evalTypeLabelMap[EVAL_TYPES.QA];

  const removeQueryParam = async (key) => {
    if (!(key in route.query)) return;
    const nextQuery = { ...route.query };
    delete nextQuery[key];
    await router.replace({
      path: route.path,
      query: nextQuery,
    });
  };

  const buildAutosaveAlertMessage = (previewData) => {
    const formatted = formatAutosaveTimestamp(previewData.meta?.lastUpdated);
    const taskCount = previewData.tasks.length;
    const criteriaCount = previewData.tasks.reduce((sum, task) => sum + (task.criteria?.length ?? 0), 0);
    const referenceFileCount = previewData.tasks.reduce((sum, task) => sum + (task.references?.length ?? 0), 0);
    let formattedDate = '';
    if (formatted) {
      formattedDate = `<span class="font-normal">Last saved ${formatted}</span>`;
    }
      const parts = [
        `${taskCount} tasks`,
        criteriaCount > 0 ? `<div class="inline-block text-orange-500/60">•</div> ${criteriaCount} criteria` : '',
        referenceFileCount > 0 ? `<div class="inline-block text-orange-500/60">• ${referenceFileCount}</div> files` : ''
      ].filter(Boolean);

      const summary = `<span class="font-medium">${parts.join(' ')}</span>`;
      return `
          ${formattedDate} <br>
          ${summary} <br>`;
  };

  /**
   * Load preview data from localStorage and populate form fields
   * Returns true if stored data was found and loaded, false otherwise
   */
  const loadPreviewFromStorage = async () => {
    try {
      let previewData = pendingPreviewData.value;
      if (!previewData) {
        const storedPreview = await loadRecoverySessionDataFromCache();
        previewData = parsePreviewData(storedPreview);
      }
      if (previewData) {
        applyPreviewData(previewData);
        pendingPreviewData.value = null;
        return true;
      }
    } catch (e) {
      console.error('Error loading preview from localStorage:', e);
    }
    return false;
  };

  const promptAutosaveChoice = async () => {
    try {
      const storedPreview = await loadRecoverySessionDataFromCache();
      const previewData = parsePreviewData(storedPreview);
      if (previewData) {
        pendingPreviewData.value = previewData;
        const draftEvalType = isValidEvalType(previewData.evalType) ? previewData.evalType : EVAL_TYPES.QA;
        pendingPreviewEvalType.value = draftEvalType;

        const isTypeMismatch = draftEvalType !== props.evalType;
        if (isTypeMismatch) {
          const currentTypeLabel = getEvalTypeLabel(props.evalType);
          const draftTypeLabel = getEvalTypeLabel(draftEvalType);
          autosaveAlertTitle.value = `${draftTypeLabel} draft found`;
          autosaveAlertMessage.value = `
            ${buildAutosaveAlertMessage(previewData)}
            <span class="font-normal">This draft was created as <span class="font-medium">${draftTypeLabel}</span>. Continuing will switch from <span class="font-medium">${currentTypeLabel}</span> to <span class="font-medium">${draftTypeLabel}</span> to preserve mode-specific settings.</span>
          `;
          autosaveAlertConfirmText.value = `<span class="font-medium">Continue Draft</span><span class="font-medium">Switch to ${draftTypeLabel} and restore this draft.</span>`;
          autosaveAlertCancelText.value = `<span class="font-medium">Start new ${currentTypeLabel} eval</span><span class="font-medium">Discard the saved draft and stay in ${currentTypeLabel} mode.</span>`;
        } else {
          autosaveAlertTitle.value = 'Saved draft found';
          autosaveAlertMessage.value = buildAutosaveAlertMessage(previewData);
          autosaveAlertConfirmText.value = '<span class="font-medium">Continue Draft</span><span class="font-medium">Pickup where you left off.</span>';
          autosaveAlertCancelText.value = '<span class="font-medium">Start new eval</span><span class="font-medium">Discard the saved draft and begin with an empty eval.</span>';
        }
        showAutosaveAlert.value = true;
        isAutosaveChoicePending.value = true;
        return true;
      }
    } catch (e) {
      console.error('Error checking for autosaved data:', e);
    }
    pendingPreviewEvalType.value = null;
    isAutosaveChoicePending.value = false;
    return false;
  };

  const maybeRestoreDraftFromQuery = async () => {
    if (route.query.restoreDraft !== '1') return false;
    const loaded = await loadPreviewFromStorage();
    await removeQueryParam('restoreDraft');
    return loaded;
  };

  const handleUseAutosavedData = async () => {
    showAutosaveAlert.value = false;
    isAutosaveChoicePending.value = false;

    if (pendingPreviewEvalType.value && pendingPreviewEvalType.value !== props.evalType) {
      await router.replace({
        path: route.path,
        query: {
          ...route.query,
          evalType: pendingPreviewEvalType.value,
          restoreDraft: '1',
        },
      });
      return;
    }

    const loaded = await loadPreviewFromStorage();
    if (!loaded) {
      initializeForm();
    }
  };

  const handleStartFreshEvalBuilder = async () => {
    showAutosaveAlert.value = false;
    isAutosaveChoicePending.value = false;
    pendingPreviewData.value = null;
    pendingPreviewEvalType.value = null;
    await clearPreviewEvalPayload();
    initializeForm();
    await setDefaultOwnerIfNeeded();
  };

  const setDefaultOwnerIfNeeded = async () => {
    if (editingDatasetId.value || selectedOwner.value || !userInfo.value?.id) {
      return;
    }
    if (isAdmin.value) {
      selectedOwner.value = userInfo.value.id;
      return;
    }
    const seller = await fetchSellerById(userInfo.value.id).catch(() => null);
    selectedOwner.value = seller?.user?.id || userInfo.value.id;
  };
  
  // Watchers e Lifecycle
  
  onMounted(async () => {
    const cleanup = initializeEvalBuilder();

    if (route.query.datasetId) {
      await loadDataset(route.query.datasetId);
    } else {
      const restoredFromQuery = await maybeRestoreDraftFromQuery();
      if (!restoredFromQuery) {
        const hasPrompted = await promptAutosaveChoice();
        if (!hasPrompted) {
          initializeForm();
        }
      }
    }
  
    if (isAdmin.value) {
      fetchSellers().then((sellers) => {
        ownersList.value = sellers.map((seller) => {
          if (seller) {
            return {
              value: seller.user_id,
              profilePicture: seller.user?.profile_pic,
              label: seller.user?.username
            }
          }
        }).filter(Boolean);
      });
    }
    await setDefaultOwnerIfNeeded();
  
    onUnmounted(() => cleanup());
  });

  onUnmounted(async () => {
    await flushEvalBuilderRecoverySessionToCache(); 
  });
  
  watch(() => route.query.datasetId, async (newDatasetId, oldDatasetId) => {
    const hasValidId = newDatasetId && typeof newDatasetId === 'string' && newDatasetId.trim() !== '';
    
    if (hasValidId && newDatasetId !== oldDatasetId) {
      await loadDataset(newDatasetId);
    } else if (!hasValidId) {
      const restoredFromQuery = await maybeRestoreDraftFromQuery();
      if (!restoredFromQuery) {
        const hasPrompted = await promptAutosaveChoice();
        if (!hasPrompted) {
          clearVersions();
          initializeForm();
        }
      }
      
      await setDefaultOwnerIfNeeded();
    }
  });
  
  watch(evalStatus, (newStatus) => {
    if (newStatus === 'error' && evalError.value) {
      console.error(evalError.value);
    } 
  });
  
  watch([tasks, evalName, selectedOwner, () => props.evalType], () => {
    if (editingDatasetId.value || isAutosaveChoicePending.value) return;
    try {
      const now = new Date().toISOString();
      const payload = {
        evalName: evalName.value,
        owner: userInfo.value?.username || username.value,
        ownerId: selectedOwner.value,
        evalType: props.evalType,
        tasks: tasks.value,
        meta: { lastUpdated: now, version: 1, recoverySession: true }
      };
      
      // Update lastUpdated in UI
      lastUpdated.value = now;
      
      // Determine the key - use update if we already have data, otherwise create
      const hasExistingData = localStorage.getItem('preview_eval_payload');
      const cacheKey = hasExistingData ? 'recovery_session_data/:eval_builder_update' : 'recovery_session_data/:eval_builder_create';
      
      // Save to localStorage immediately; cache writes are throttled internally.
      saveEvalPreviewDataToStorage(payload, cacheKey);
    } catch (error) {
      console.error('Error saving eval preview data:', error);
    }
  }, { deep: true });

  watch(selectedVersionId, (newVersionId, oldVersionId) => {
    if (newVersionId !== oldVersionId) {
      blockedEditEval.value = Boolean(
        isEditing.value && lastVersion.value && newVersionId && newVersionId !== lastVersion.value
      );
      loadDataset(editingDatasetId.value, newVersionId);
    }
  }, { deep: true });

  watch(lastVersion, (newLastVersion) => {
    if (!isEditing.value) return;
    if (!newLastVersion || !selectedVersionId.value) {
      blockedEditEval.value = false;
      return;
    }
    blockedEditEval.value = Boolean(selectedVersionId.value !== newLastVersion);
  });
  
  
  function getTaskDisplayName(task) {
    if (task.prompt) {
      const ellipsis = task.prompt.length > 50 ? '...' : '';
      return task.prompt.substring(0, 50) + ellipsis;
    }
    return 'Untitled task';
  }

  function validateTaskRequirements(task) {
    const taskName = getTaskDisplayName(task);
    const criteria = task.criteria || [];
    if (criteria.length === 0) {
      return {
        valid: false,
        message: `Task "${taskName}" needs at least one grading rubric criterion.`
      };
    }

    if (!task.answer || typeof task.answer !== 'string' || task.answer.trim() === '') {
      return {
        valid: false,
        message: `Task "${taskName}" is missing a Golden Answer.`
      };
    }

    return { valid: true };
  }

  function validateTaskWeights(task) {
    const criteria = task.criteria || [];
    if (criteria.length === 0) {
      return { valid: true };
    }

    const hasZeroWeight = criteria.some(c => (c.weight ?? 0) === 0);
    if (hasZeroWeight) {
      const taskName = getTaskDisplayName(task);
      return {
        valid: false,
        message: `Cannot create eval with criteria having 0% weight. Please remove or adjust the weight of criteria with 0% in task "${taskName}".`
      };
    }

    const rawSum = criteria.reduce((sum, c) => sum + (c.weight ?? 0), 0);

    const tolerance = 0.01; 
    if (Math.abs(rawSum - 100) > tolerance) {
      const displayTotal = Math.round(rawSum * 100) / 100;
      const taskName = getTaskDisplayName(task);
      
      return {
        valid: false,
        message: `Weights must sum to 100%. The task "${taskName}" currently sums to ${displayTotal}%.`
      };
    }

    return { valid: true };
  }

  function validateCriteriaContent(task) {
    const criteria = task.criteria || [];
    
    if (criteria.length === 0) {
      return { valid: true };
    }

    for (let i = 0; i < criteria.length; i++) {
      const c = criteria[i];
      const displayIndex = i + 1;

      if (!c.name || !c.name.trim()) {
        return {
          valid: false,
          message: `Criterion #${displayIndex} in task "${getTaskDisplayName(task)}" is missing a Name.`
        };
      }

      if (!c.semanticPrompt || !c.semanticPrompt.trim()) {
        return {
          valid: false,
          message: `Criterion "${c.name}" in task "${getTaskDisplayName(task)}" is missing the Explicit Grading Criterion description.`
        };
      }
    }

    return { valid: true };
  }

  function validateAll() {
    for (const task of tasks.value) {
      const taskRequirementsResult = validateTaskRequirements(task);
      if (!taskRequirementsResult.valid) return taskRequirementsResult;

      const weightResult = validateTaskWeights(task);
      if (!weightResult.valid) return weightResult;

      if (!blockedEditEval.value) {
        const contentResult = validateCriteriaContent(task);
        if (!contentResult.valid) return contentResult;
      }
    }
    return { valid: true };
  }
  
  async function saveEval() {
    if (blockedEditEval.value) return;
    if (!selectedOwner.value) return;
    
    if (!evalName.value || !evalName.value.trim()) {
      addToast('Eval name is required', 'error');
      return;
    }
  if (!blockedEditEval.value) {
    const validation = validateAll();
    if (!validation.valid) {
      addToast(validation.message, 'error');
      return;
    }
  }
    let result;
    if (!blockedEditEval.value && isEditing.value) {
      result = await updateEval(props.evalType);
    } else {
      result = await createEval(isAdmin.value, createEvalWithWebSocket, props.evalType);
    }
  
    if (result === true && createdDatasetId.value) {
      alertTitle.value = 'Eval Created Successfully!';
      alertMessage.value = 'Would you like to view model results for your eval or create a listing?';
      alertType.value = 'success';
      showAlert.value = true;
    }

    if (result === true && blockedEditEval.value) {
      alertTitle.value = 'Eval Updated';
      alertMessage.value = 'Thank you for updating your eval. All new evaluations will use this new version.<br><br>Do you also want to update the listing?';
      alertType.value = 'success';
      showUpdateListingAlert.value = true;
    }
  }
  
  const handleAlertConfirm = () => {
    if (!createdDatasetId.value) return;
    showAlert.value = false;
    router.push({
      name: 'dataset-listing-create',
      params: { datasetId: createdDatasetId.value.toString() },
      query: { isEvalDataset: 'true' }
    });
  };
  
  const handleAlertCancel = () => {
    showAlert.value = false;
    router.push('/creator-profile?page=eval-annotate&subpage=eval-annotate');
  };

  const handleUpdateListingConfirm = () => {
    router.push('/creator-profile?page=evals');
  };

  const handleUpdateListingCancel = () => {
    showUpdateListingAlert.value = false;
    router.push('/creator-profile?page=eval-datasets&subpage=eval-datasets');
  };

  const validateOwnerUpdateForm = () => {
    if (isAdmin.value && !selectedOwner.value) {
      addToast('Please select an owner', 'error');
      return false;
    }

    if (!evalName.value || !evalName.value.trim()) {
      addToast('Eval name is required', 'error');
      return false;
    }

    return true;
  };

  const prepareEvalInfo = () => {
    let file_name = "";
    let file_size = 0;
    let file_extension = "";

    if (coreDatasetFile.value) {
      const meta = getFileMetadata(coreDatasetFile.value);
      file_name = meta.fullFileName;
      file_size = meta.size;
      file_extension = meta.fileExtension;
    } else if (existingCoreDatasetInfo.value) {
      file_name = existingCoreDatasetInfo.value.name || "";
      file_size = existingCoreDatasetInfo.value.size || 0;
      file_extension = existingCoreDatasetInfo.value.extension || "";
    }

    return {
      dataset: {
        file_name: file_name,
        file_size: file_size,
        file_extension: file_extension,
        name: evalName.value,
        description: 'Updated via Eval Builder',
        owner_user_id: selectedOwner.value,
      },
      comment: 'Dataset owner updated via Eval Builder'
    };
  };

  const handleUpdateOwnerConfirm = () => {
    if (!isAdmin.value) {
      addToast('Unauthorized: Admin access required', 'error');
      return;
    }
    
    showUpdateOwnerAlert.value = true;
    alertTitle.value = 'Confirm Owner Update';
    alertMessage.value = `Are you sure you want to update the owner of this eval from <span class="font-bold">${originalOwnerName.value}</span> to <span class="font-bold">${username.value}</span>? This action will change the eval ownership.`;
    alertType.value = 'warning';
  };

  const confirmUpdateOwner = async () => {
    if (!isAdmin.value) {
      addToast('Unauthorized: Admin access required', 'error');
      return;
    }
    
    if (!validateOwnerUpdateForm()) {
      showUpdateOwnerAlert.value = false;
      return;
    }
    
    showUpdateOwnerAlert.value = false;
    isSaving.value = true;
    
    try {
      const evalInfo = prepareEvalInfo();
      const response = await updateDatasetOwner(editingDatasetId.value, evalInfo);
      if (response.status === 200 || response.status === 201) {
        originalOwner.value = selectedOwner.value;
        originalOwnerName.value = username.value;
      } else {
        throw new Error(response.message || 'Failed to update owner');
      }
    } catch (error) {
      console.error('Error updating eval owner:', error);
      addToast(error?.message || error || 'Error updating owner', 'error');
      selectedOwner.value = originalOwner.value;
    } finally {
      isSaving.value = false;
    }
  };

  const cancelUpdateOwner = () => {
    showUpdateOwnerAlert.value = false;
    selectedOwner.value = originalOwner.value;
  };

  const handleOwnerChange = () => {
    if (!isAdmin.value || !blockedEditEval.value) return;
    if (blockedEditEval.value) return;
    
    if (selectedOwner.value !== originalOwner.value) {
      handleUpdateOwnerConfirm();
    }
  };

  const isShowRevertAlert = ref(false);
  const isReverting = ref(false);

  const showRevertAlert = () => {
    alertTitle.value = 'Confirm Revert';
    alertMessage.value = 'Are you sure you want to revert the eval to this version?<br>This action will reverse all subsequent versions.';
    alertType.value = 'warning';
    isShowRevertAlert.value = true;
  };
  
  const handleRevertConfirm = async () => {
    isReverting.value = true;
    await revertVersion();
    isReverting.value = false;
    isShowRevertAlert.value = false;
    clearAlertInfos()
  };

  const handleRevertCancel = () => {
    isShowRevertAlert.value = false;
  };

  const clearAlertInfos = () => {
    alertTitle.value = ''
    alertMessage.value = '';
    alertType.value = '';

  }

  const formatUsername = (username) => {
    if (username.length > 10) {
      return username.substring(0, 10) + '...';
    }
    return username;
  };

  const handlePreviousTask = () => {
    const currentIndex = tasks.value.findIndex(t => t.id === activeTaskId.value);
    if (currentIndex > 0) {
      activeTaskId.value = tasks.value[currentIndex - 1].id;
    }
  };

  const handleNextTask = () => {
    const currentIndex = tasks.value.findIndex(t => t.id === activeTaskId.value);
    if (currentIndex < tasks.value.length - 1) {
      activeTaskId.value = tasks.value[currentIndex + 1].id;
    }
  };

  const handleChangeEvalType = () => {
    if (!canChangeEvalType.value || isLoading.value) return;
    showChangeEvalTypeAlert.value = true;
  };

  const cancelChangeEvalType = () => {
    showChangeEvalTypeAlert.value = false;
  };

  const confirmChangeEvalType = async () => {
    if (!canChangeEvalType.value || isLoading.value) return;
    showChangeEvalTypeAlert.value = false;
    const nextQuery = { ...route.query };
    delete nextQuery.evalType;
    delete nextQuery.restoreDraft;
    await router.replace({
      path: route.path,
      query: nextQuery,
    });
  };

</script>


  
  
<template>
    <AutoSaveModal
      v-if="showAutosaveAlert"
      :show="showAutosaveAlert"
      :title="autosaveAlertTitle"
      :message="autosaveAlertMessage"
      :confirmText="autosaveAlertConfirmText"
      :cancelText="autosaveAlertCancelText"
      @confirm="handleUseAutosavedData"
      @close="handleStartFreshEvalBuilder"
      :showCloseBtn="false"
      dataTestId="eval-autosave-alert"
    />
    <!-- Create listing: after eval created, prompt to create listing (showAlert / handleAlertConfirm / handleAlertCancel) -->
    <Alert
      :show="showAlert"
      :title="alertTitle"
      :message="alertMessage"
      :type="alertType"
      confirmText="Yes, create listing"
      cancelText="Go to results"
      @confirm="handleAlertConfirm"
      @close="handleAlertCancel"
      :showCloseBtn="false"
      dataTestId="eval-create-listing-alert"
    />
    <Alert
      :show="showUpdateListingAlert"
      :title="alertTitle"
      :message="alertMessage"
      :type="alertType"
      confirmText="Go to Eval Listing"
      cancelText="Cancel"
      @confirm="handleUpdateListingConfirm"
      @close="handleUpdateListingCancel"
      :showCloseBtn="false"
      dataTestId="eval-update-listing-alert"
    />
    <!-- Change eval type: switch between Q&A and Agentic (showChangeEvalTypeAlert / confirmChangeEvalType / cancelChangeEvalType) -->
    <Alert
      :show="showChangeEvalTypeAlert"
      title="Change eval type?"
      :message="changeEvalTypeAlertMessage"
      type="warning"
      confirmText="Change Eval Type"
      cancelText="Keep Editing"
      @confirm="confirmChangeEvalType"
      @close="cancelChangeEvalType"
      :showCloseBtn="false"
      dataTestId="eval-change-type-alert"
    />
    <Alert
      v-if="showUpdateOwnerAlert && isAdmin"
      :show="showUpdateOwnerAlert"
      :title="alertTitle"
      :message="alertMessage"
      :type="alertType"
      confirmText="Update Owner"
      cancelText="Cancel"
      @confirm="confirmUpdateOwner"
      @close="cancelUpdateOwner"
      :showCloseBtn="false"
      loadingText="Updating owner..."
    />

    <Alert :show="isShowRevertAlert" :title="alertTitle" :message="alertMessage" :type="alertType"
      confirmText="Revert" cancelText="Cancel" @confirm="handleRevertConfirm"
      @close="handleRevertCancel" :showCloseBtn="false" dataTestId="eval-revert-alert" :isLoading="isReverting" loadingText="Reverting..." />
      <PreviewResults v-if="isPreview" 
    />
    <div class="w-full h-[calc(100vh-4rem)] flex flex-col overflow-hidden" v-else>
      <header
        class="h-14 border-b border-echoes-border-gray/40 flex items-center justify-between p-3 gap-3 flex-shrink-0">
        <div class="flex items-center gap-4">
          <h1 class="text-lg font-bold bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit">
            {{ isEditing ? 'Edit Eval' : (isAgentic ? 'Agentic Eval Builder' : 'Eval Builder') }}</h1>
          <div class="flex items-center gap-2">
            <template v-if="isAdmin">
              <div class="max-w-56">
                <SelectInput id="owner" v-model="selectedOwner" :options="ownersList" placeholder="Select owner"
                  class="w-full" :showProfilePicture="true" :disabled="isLoading || blockedEditEval" 
                  @change="handleOwnerChange" @update:modelValue="handleOwnerChange" dataTestId="owner-select" />
              </div>
            </template>
            <template v-else>
              <div class="user-badge-eval">
                <div class="user-avatar-eval">
                  <img v-if="profilePic" :src="profilePic" alt="User avatar" />
                  <User v-else class="w-full h-full p-1 text-gray-100" />
                </div>
                <div class="whitespace-nowrap">{{ formatUsername(username) }}</div>
              </div>
            </template>
            <div class="separator-eval">/</div>
            <TextInput id="evalName" v-model="evalName" placeholder="Eval name..." class="w-64 2xl:w-96" :disabled="isLoading || blockedEditEval" dataTestId="eval-name-input"/>
            <div class="flex justify-between gap-2 items-center">
              <div class="flex col-span-1 flex-col">
                <div class="flex items-center gap-2" v-if="versionsOptions.length >= 1">
                  <div class="flex items-center gap-1">
                    <select-input
                      v-model="selectedVersionId"
                      :options="versionsOptions"
                      label="version"
                      value="value"
                      placeholder="Select version"
                      :disabled="isLoading || versionsOptions.length === 1"
                      dataTestId="version-select"
                    />
                  </div>
                </div>
              </div>
             
            </div>
          </div>
        </div>
        <div class="flex items-center gap-6">
          <div class="flex items-center justify-center">
          <span class="text-white/60 font-semibold text-sm text-center">Auto-saved <br>{{ lastUpdated ? new Date(lastUpdated).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '' }}</span>
        </div>
        <button
          v-if="canChangeEvalType"
          class="rounded-full py-1.5 px-3 text-xs font-medium border border-echoes-border-gray bg-echoes-background-gray/40 text-white/70 transition-colors duration-200"
          :class="isLoading ? 'cursor-not-allowed opacity-50' : 'hover:bg-echoes-background-gray/70 hover:text-white/90 cursor-pointer'"
          :disabled="isLoading"
          @click="handleChangeEvalType"
          data-testid="change-eval-type-button"
        >
          Change Eval Type
        </button>
        <div class="flex items-center gap-3">
          <button :class="[
            'rounded-full py-1.5 px-4 font-semibold text-sm transition-all duration-200 flex items-center gap-2',
            isRevertVersionMode
              ? (isReverting ? 'bg-echoes-primary/50 border border-echoes-primary/50 text-white/70 cursor-not-allowed' : 'bg-echoes-primary border border-echoes-primary text-white cursor-pointer hover:bg-echoes-primary/90 hover:border-echoes-primary/90')
              : (isLoading || !selectedOwner || savedSuccessfully || blockedEditEval
                ? 'bg-green-600/50 border border-green-600/50 text-white/70 cursor-not-allowed'
                : 'bg-green-600 border border-green-600 text-white cursor-pointer hover:bg-green-700 hover:border-green-700')
          ]" @click="isRevertVersionMode ? showRevertAlert() : saveEval()" :disabled="isRevertVersionMode ? isReverting : (isLoading || !selectedOwner || savedSuccessfully || blockedEditEval)"
            :title="isRevertVersionMode ? 'Revert to this version' : (!selectedOwner ? 'Please select an owner before saving' : savedSuccessfully ? 'Eval saved successfully' : '')"
            data-testid="save-eval-button">
            <div v-if="isLoading || isReverting"
              class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            <span>{{ isLoadingDataset ? 'Loading...' : isReverting ? 'Reverting...' : isSaving ? 'Saving...' : savedSuccessfully ? 'Saved!' :
              saveButtonText
              }}</span>
            <i :class="isRevertVersionMode ? 'mdi mdi-backup-restore' : 'mdi mdi-chevron-right-circle-outline'" class="text-base"></i>
          </button>
        </div>


        </div>
        
      </header>
      <div class="flex h-full overflow-hidden">
        <div class="w-1/4 2xl:w-1/5 overflow-y-auto" :class="{ 'pointer-events-none': isLoading }">
          <Sidebar 
            :tasks="tasks" 
            :activeTaskId="activeTaskId" 
            :exampleTask="selectedExampleTask"
            :answeredCount="answeredCount"
            :isAgentic="isAgentic"
            :isEditing="isLoading" 
            :disableAdd="blockedEditEval || isLoading"
            :showExampleMode="showExampleMode"
            @setActiveTaskId="activeTaskId = $event" 
            @addTask="addTask"
            @toggleAnsweredFilter="handleToggleAnsweredFilter" />
        </div>
        <main class="p-2 flex-1 overflow-y-auto min-h-0" :class="{ 'pointer-events-none': isLoading }">
          <TaskEditor v-if="activeTask" :task="activeTask" :canDelete="tasks.length > 1" :isEditing="blockedEditEval || isLoading"
            :isAgentic="isAgentic"
            :isFirstTask="tasks.length > 0 && tasks[0].id === activeTask.id"
            :isLastTask="tasks.length > 0 && tasks[tasks.length - 1].id === activeTask.id"
            :isExampleMode="showExampleMode && activeTask.id === selectedExampleTask.id"
            @updateTask="updateTask" @deleteTask="deleteTask" 
            @previousTask="handlePreviousTask" @nextTask="handleNextTask" />
          <div v-else-if="tasks.length === 0" class="flex flex-col items-center justify-center h-full text-center">
            <div class="mb-6">
              <div class="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
            </div>
            <h3 class="text-xl font-semibold text-white/90 mb-2">Loading...</h3>
            <p class="text-white/50 text-base mb-6 max-w-md">
              Please wait while we load your tasks.
            </p>
          </div>
          <div v-else class="text-white/70 text-base p-4">Select a task to edit.</div>
        </main>
      </div>
    </div>
  </template>

<style scoped>
.user-badge-eval {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 0.75rem;
  background: rgba(43, 45, 51, 0.3);
  color: white;
  border: 1px solid rgba(43, 45, 51, 1);
}

.user-avatar-eval {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  overflow: hidden;
  background: #FE3E01;
}

.user-avatar-eval img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.separator-eval {
  margin: 0 0.25rem;
  color: rgb(156 163 175);
  font-size: 1.5rem;
  align-self: center;
  flex-shrink: 0;
}


.core-dataset-upload-wrapper {
  min-width: 220px;
}

.core-dataset-upload-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0.75rem;
  border-radius: 0.75rem;
  height: 2.75rem;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s;
  overflow: visible;
}

.core-dataset-upload-label:hover {
  background: #0a0a0a;
  border-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.core-dataset-upload-label.has-file {
  color: white;
}

.core-dataset-upload-label.has-error {
  border-color: rgba(239, 68, 68, 0.5);
}

.core-dataset-upload-label>div:first-child {
  overflow: visible !important;
  position: relative;
}

.core-dataset-upload-label .group {
  overflow: visible !important;
  position: relative;
  z-index: 1;
}
</style>
